import { useState, useEffect } from 'react';
import Navbar     from './components/Navbar';
import Hero       from './components/Hero';
import Skills     from './components/Skills';
import Experience from './components/Experience';
import Projects   from './components/Projects';
import Contact    from './components/Contact';
import Preloader  from './Preloader';
import CustomCursor from './CustomCursor';

/* Deterministic "random" so particles don't shift on re-render */
const seededRandom = (seed) => {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

const COLORS = ['#00f5ff', '#ff00de', '#00ff88', '#7c3aed'];

function buildParticles(count = 55) {
  const rng = seededRandom(42);
  return Array.from({ length: count }, (_, i) => ({
    id:       i,
    cx:       rng() * 100,
    cy:       rng() * 100,
    r:        rng() * 2 + 0.5,
    opacity:  rng() * 0.45 + 0.08,
    color:    COLORS[i % COLORS.length],
    duration: rng() * 22 + 14,
    delay:    rng() * 10,
  }));
}

const PARTICLES = buildParticles();

function App() {
  const [loading, setLoading] = useState(true);

  const handlePreloaderComplete = () => {
    document.querySelector('.preloader').classList.add('hidden');
    setTimeout(() => setLoading(false), 500);
  };

  /* Global scroll-reveal observer – runs once after full render */
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const targets = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    targets.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [loading]);

  return (
    <>
      <CustomCursor />

      {loading ? (
        <Preloader onComplete={handlePreloaderComplete} />
      ) : (
        <div className="relative min-h-screen bg-[#050510] text-slate-200 overflow-x-hidden">

          {/* ── Animated particle / grid background ── */}
          <svg
            className="fixed inset-0 w-full h-full pointer-events-none select-none"
            style={{ zIndex: 0 }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <radialGradient id="bg-radial" cx="55%" cy="25%" r="65%">
                <stop offset="0%"   stopColor="#0a0a2e" stopOpacity="1" />
                <stop offset="100%" stopColor="#050510" stopOpacity="1" />
              </radialGradient>
              <pattern id="dot-grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path
                  d="M 50 0 L 0 0 0 50"
                  fill="none"
                  stroke="rgba(0,245,255,0.035)"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>

            <rect width="100%" height="100%" fill="url(#bg-radial)" />
            <rect width="100%" height="100%" fill="url(#dot-grid)" />

            {PARTICLES.map((p) => (
              <circle
                key={p.id}
                cx={`${p.cx}%`}
                cy={`${p.cy}%`}
                r={p.r}
                fill={p.color}
                opacity={p.opacity}
                style={{
                  animation: `particle-drift ${p.duration}s ${p.delay}s ease-in-out infinite alternate`,
                  filter: `blur(0.4px) drop-shadow(0 0 3px ${p.color})`,
                }}
              />
            ))}
          </svg>

          {/* ── Corner accent arcs ── */}
          <div
            className="fixed top-0 right-0 pointer-events-none select-none"
            style={{ zIndex: 0, opacity: 0.07 }}
          >
            <svg width="420" height="420" viewBox="0 0 420 420">
              <circle cx="420" cy="0"   r="210" fill="none" stroke="#00f5ff" strokeWidth="1" />
              <circle cx="420" cy="0"   r="150" fill="none" stroke="#00f5ff" strokeWidth="0.6" />
              <circle cx="420" cy="0"   r="90"  fill="none" stroke="#ff00de" strokeWidth="0.6" />
            </svg>
          </div>
          <div
            className="fixed bottom-0 left-0 pointer-events-none select-none"
            style={{ zIndex: 0, opacity: 0.07 }}
          >
            <svg width="320" height="320" viewBox="0 0 320 320">
              <circle cx="0" cy="320" r="160" fill="none" stroke="#ff00de" strokeWidth="1" />
              <circle cx="0" cy="320" r="110" fill="none" stroke="#7c3aed" strokeWidth="0.6" />
            </svg>
          </div>

          {/* ── Page content ── */}
          <div className="relative" style={{ zIndex: 1 }}>
            <Navbar />
            <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
              <Hero />
              <Skills />
              <Experience />
              <Projects />
              <Contact />
            </main>

            {/* Footer */}
            <footer className="border-t border-cyan-900/20 py-8 text-center mt-10">
              <p className="text-slate-500 text-sm font-mono">
                Crafted with{' '}
                <span style={{ color: '#ff00de' }}>&#9829;</span>
                {' '}by{' '}
                <span style={{ color: '#00f5ff' }}>Abhiram Gargeya</span>
              </p>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
